import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  InputField,
  Modal,
  Select,
  ToggleSwitch,
} from '@enamelai/design-system'
import {
  ICaptureDevice,
  ICaptureDevicePayload,
  PatientRouting,
} from '@dataTypes/dicom-capture.type'
import { IOperatory } from '@dataTypes/appointment.type'
import {
  AE_TITLE_REGEX,
  buildOperatoryOptions,
  fromOperatoryOption,
  isValidIpCidrList,
  PATIENT_ROUTING_OPTIONS,
  toOperatoryOption,
} from './dicomCapture.data'

interface DeviceFormData {
  label: string
  calling_ae_title: string
  allowed_remote_ips: string
  default_modality: string
  operatory_id: string
  default_patient_routing: PatientRouting
  enabled: boolean
}

const emptyForm = (): DeviceFormData => ({
  label: '',
  calling_ae_title: '',
  allowed_remote_ips: '',
  default_modality: '',
  operatory_id: '',
  default_patient_routing: 'from_dicom_tag',
  enabled: true,
})

const deviceToForm = (device: ICaptureDevice): DeviceFormData => ({
  label: device.label,
  calling_ae_title: device.calling_ae_title,
  allowed_remote_ips: device.allowed_remote_ips ?? '',
  default_modality: device.default_modality ?? '',
  operatory_id: device.operatory_id ?? '',
  default_patient_routing: device.default_patient_routing,
  enabled: device.enabled,
})

type AddDeviceModalProps = {
  isOpen: boolean
  clinicId: string
  operatories: IOperatory[]
  mode?: 'add' | 'edit'
  device?: ICaptureDevice | null
  onRequestClose: () => void
  onSubmit: (payload: ICaptureDevicePayload, editId?: string) => Promise<void>
  isSubmitting?: boolean
}

const AddDeviceModal: React.FC<AddDeviceModalProps> = ({
  isOpen,
  clinicId,
  operatories,
  mode = 'add',
  device = null,
  onRequestClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<DeviceFormData>(emptyForm())
  const [errors, setErrors] = useState<Record<string, string>>({})
  const wasOpenRef = useRef(false)

  const operatoryOptions = useMemo(
    () => buildOperatoryOptions(operatories),
    [operatories],
  )

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen
    if (!justOpened) return
    setFormData(device ? deviceToForm(device) : emptyForm())
    setErrors({})
  }, [isOpen, device])

  const handleChange = useCallback(
    <K extends keyof DeviceFormData>(
      field: K,
      value: DeviceFormData[K],
    ): void => {
      setFormData((prev) => ({ ...prev, [field]: value }))
      setErrors((prev) => {
        if (!prev[field]) return prev
        const next = { ...prev }
        delete next[field as string]
        return next
      })
    },
    [],
  )

  const validate = useCallback((): boolean => {
    const next: Record<string, string> = {}
    if (!formData.label.trim()) next.label = 'Label is required'
    const aet = formData.calling_ae_title.trim().toUpperCase()
    if (!aet) {
      next.calling_ae_title = 'Calling AE title is required'
    } else if (!AE_TITLE_REGEX.test(aet)) {
      next.calling_ae_title =
        'AE title must be uppercase alphanumeric, 1-16 characters'
    }
    if (
      formData.allowed_remote_ips.trim() &&
      !isValidIpCidrList(formData.allowed_remote_ips)
    ) {
      next.allowed_remote_ips =
        'Enter comma-separated IPv4 addresses or CIDR blocks (e.g. 10.0.0.0/24)'
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }, [formData])

  const handleClose = useCallback((): void => {
    setFormData(emptyForm())
    setErrors({})
    onRequestClose()
  }, [onRequestClose])

  const handleSave = useCallback(async (): Promise<void> => {
    if (!validate()) return
    const payload: ICaptureDevicePayload = {
      clinic_id: clinicId,
      operatory_id: formData.operatory_id || null,
      label: formData.label.trim(),
      calling_ae_title: formData.calling_ae_title.trim().toUpperCase(),
      allowed_remote_ips: formData.allowed_remote_ips.trim() || null,
      default_modality: formData.default_modality.trim() || null,
      default_patient_routing: formData.default_patient_routing,
      enabled: formData.enabled,
    }
    try {
      await onSubmit(payload, device?.capture_device_id)
      handleClose()
    } catch {
      // Parent surfaces the error toast; keep the modal open for corrections.
    }
  }, [clinicId, device, formData, handleClose, onSubmit, validate])

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="md"
      title={mode === 'edit' ? 'Edit capture device' : 'Add capture device'}
      closeOnBackdrop={false}
      className="dc-modal"
      secondaryLabel="Cancel"
      onSecondaryClick={handleClose}
      primaryLabel={mode === 'edit' ? 'Save' : 'Add'}
      primaryDisabled={isSubmitting}
      onPrimaryClick={() => {
        void handleSave()
      }}
    >
      <form
        className="dc-modal__form"
        onSubmit={(e): void => {
          e.preventDefault()
          void handleSave()
        }}
      >
        <div className="dc-modal__row">
          <InputField
            variant="text"
            label="Label"
            placeholder="e.g. Op 1 X-ray"
            value={formData.label}
            onChange={(e) => handleChange('label', e.target.value)}
            error={!!errors.label}
            helperText={errors.label}
            fullWidth
          />
          <InputField
            variant="text"
            label="Calling AE title"
            placeholder="e.g. OP1XRAY"
            value={formData.calling_ae_title}
            onChange={(e) =>
              handleChange('calling_ae_title', e.target.value.toUpperCase())
            }
            error={!!errors.calling_ae_title}
            helperText={errors.calling_ae_title}
            fullWidth
          />
        </div>

        <InputField
          variant="text"
          label="Allowed remote IPs (optional)"
          placeholder="e.g. 10.0.0.5, 10.0.1.0/24"
          value={formData.allowed_remote_ips}
          onChange={(e) => handleChange('allowed_remote_ips', e.target.value)}
          error={!!errors.allowed_remote_ips}
          helperText={
            errors.allowed_remote_ips ||
            'Comma-separated IPv4 addresses or CIDR blocks. Leave blank to allow any.'
          }
          fullWidth
        />

        <Select
          label="Operatory / chair (optional)"
          options={operatoryOptions}
          value={toOperatoryOption(formData.operatory_id)}
          onChange={(value) =>
            handleChange('operatory_id', fromOperatoryOption(String(value)))
          }
          helperText="When assigned, images route to the patient currently open at this chair (active-patient routing). The routing below is the fallback used when no patient is open."
        />

        <div className="dc-modal__row">
          <InputField
            variant="text"
            label="Default modality (optional)"
            placeholder="e.g. IO, PX, CR"
            value={formData.default_modality}
            onChange={(e) => handleChange('default_modality', e.target.value)}
            fullWidth
          />
          <Select
            label="Patient routing (fallback)"
            options={PATIENT_ROUTING_OPTIONS}
            value={formData.default_patient_routing}
            onChange={(value) =>
              handleChange('default_patient_routing', value as PatientRouting)
            }
          />
        </div>

        <div className="dc-modal__toggles">
          <label className="dc-modal__toggle" htmlFor="dicom-device-enabled">
            <ToggleSwitch
              id="dicom-device-enabled"
              checked={formData.enabled}
              onChange={(checked) => handleChange('enabled', checked)}
            />
            <span>Enabled</span>
          </label>
        </div>
      </form>
    </Modal>
  )
}

export default AddDeviceModal
